<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import {
  PromotionRequestStatus,
  type CatalogueItemPromotionRequest,
  type PaginatedResponse,
} from '@/models/Catalogue';
import {
  getPromotionRequests,
  reviewPromotionRequest,
} from '@/api/CatalogueApi';
import formatDate from '@/helpers/DateFormatterHelper';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();

const props = defineProps<{
  modelValue: boolean;
  canReview: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'reviewed'): void;
  (e: 'openItem', itemUuid: string): void;
}>();

const requests = ref<CatalogueItemPromotionRequest[]>([]);
const pagination = ref({
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
});
const statusFilter = ref<PromotionRequestStatus | 'all'>(
  PromotionRequestStatus.PENDING,
);

const showReviewDialog = ref(false);
const reviewingRequest = ref<CatalogueItemPromotionRequest | null>(null);
const reviewAction = ref<
  PromotionRequestStatus.APPROVED | PromotionRequestStatus.REJECTED
>(PromotionRequestStatus.APPROVED);
const reviewComment = ref('');
const reviewLoading = ref(false);

const statusOptions = [
  { value: 'all', title: $gettext('All') },
  { value: PromotionRequestStatus.PENDING, title: $gettext('Pending') },
  { value: PromotionRequestStatus.APPROVED, title: $gettext('Approved') },
  { value: PromotionRequestStatus.REJECTED, title: $gettext('Rejected') },
];

const totalPages = computed(() => pagination.value.totalPages);

const loadRequests = async () => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const params = {
      page: pagination.value.page,
      limit: pagination.value.limit,
      ...(statusFilter.value !== 'all' && { status: statusFilter.value }),
    };
    const response: PaginatedResponse<CatalogueItemPromotionRequest> =
      await getPromotionRequests(params);
    requests.value = response.items;
    pagination.value = {
      page: response.pagination.page,
      limit: response.pagination.limit,
      total: response.pagination.total,
      totalPages: response.pagination.totalPages,
    };
  } catch (error) {
    console.error('Failed to load promotion requests:', error);
    requests.value = [];
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleClose = () => {
  emit('update:modelValue', false);
};

const handleOpenItem = (itemUuid: string) => {
  if (showReviewDialog.value) {
    return;
  }
  emit('openItem', itemUuid);
};

const openReviewDialog = (
  request: CatalogueItemPromotionRequest,
  action: PromotionRequestStatus.APPROVED | PromotionRequestStatus.REJECTED,
) => {
  reviewingRequest.value = request;
  reviewAction.value = action;
  reviewComment.value = '';
  showReviewDialog.value = true;
};

const confirmReview = async () => {
  if (!reviewingRequest.value) {
    return;
  }

  reviewLoading.value = true;
  try {
    await reviewPromotionRequest(reviewingRequest.value.uuid, {
      status: reviewAction.value,
      ...(reviewComment.value && { comment: reviewComment.value }),
    });
    showReviewDialog.value = false;
    reviewingRequest.value = null;
    await loadRequests();
    emit('reviewed');
  } catch (error) {
    console.error('Failed to review promotion request:', error);
  } finally {
    reviewLoading.value = false;
  }
};

const cancelReview = () => {
  showReviewDialog.value = false;
  reviewingRequest.value = null;
  reviewComment.value = '';
};

const getStatusColor = (status: PromotionRequestStatus) => {
  switch (status) {
    case PromotionRequestStatus.PENDING:
      return 'info';
    case PromotionRequestStatus.APPROVED:
      return 'success';
    case PromotionRequestStatus.REJECTED:
      return 'warning';
    default:
      return 'grey';
  }
};

const getStatusText = (status: PromotionRequestStatus) => {
  switch (status) {
    case PromotionRequestStatus.PENDING:
      return $gettext('Pending');
    case PromotionRequestStatus.APPROVED:
      return $gettext('Approved');
    case PromotionRequestStatus.REJECTED:
      return $gettext('Rejected');
    default:
      return status;
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      pagination.value.page = 1;
      loadRequests();
    }
  },
);

watch(statusFilter, () => {
  pagination.value.page = 1;
  loadRequests();
});

watch(
  () => pagination.value.page,
  () => {
    loadRequests();
  },
);
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    max-width="900px"
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>{{ $gettext('Promotion Queue') }}</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="handleClose" />
      </v-card-title>
      <v-divider />
      <v-card-text>
        <div class="d-flex align-center mb-4">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            item-value="value"
            item-title="title"
            :label="$gettext('Status')"
            density="compact"
            variant="outlined"
            hide-details
            style="max-width: 200px"
          />
          <v-spacer />
          <r-btn icon="mdi-refresh" variant="outlined" @click="loadRequests">
            {{ $gettext('Refresh') }}
          </r-btn>
        </div>

        <v-alert v-if="requests.length === 0" type="info" variant="tonal">
          {{ $gettext('No promotion requests found.') }}
        </v-alert>

        <v-list v-else>
          <v-list-item
            v-for="request in requests"
            :key="request.uuid"
            class="mb-2 border rounded cursor-pointer"
            @click="handleOpenItem(request.itemUuid)"
          >
            <template #prepend>
              <v-icon icon="mdi-file-document-outline" />
            </template>

            <v-list-item-title class="font-weight-medium">
              {{ request.itemName }}
            </v-list-item-title>
            <v-list-item-subtitle>
              <div class="d-flex flex-column ga-1 mt-1">
                <span>
                  <v-icon icon="mdi-account" size="small" class="mr-1" />
                  {{ request.requesterName }} ({{ request.requesterEmail }})
                </span>
                <span>
                  <v-icon icon="mdi-calendar" size="small" class="mr-1" />
                  {{ formatDate(request.createdAt, true) }}
                </span>
              </div>
            </v-list-item-subtitle>

            <template #append>
              <div class="d-flex align-center ga-2">
                <v-chip :color="getStatusColor(request.status)" size="small">
                  {{ getStatusText(request.status) }}
                </v-chip>
                <div
                  v-if="
                    props.canReview &&
                    request.status === PromotionRequestStatus.PENDING
                  "
                  class="d-flex align-center ga-2"
                  @click.stop
                >
                  <v-btn
                    color="success"
                    variant="outlined"
                    size="small"
                    @click="
                      openReviewDialog(request, PromotionRequestStatus.APPROVED)
                    "
                  >
                    {{ $gettext('Approve') }}
                  </v-btn>
                  <v-btn
                    color="warning"
                    variant="outlined"
                    size="small"
                    @click="
                      openReviewDialog(request, PromotionRequestStatus.REJECTED)
                    "
                  >
                    {{ $gettext('Reject') }}
                  </v-btn>
                </div>
                <template v-else-if="request.comment">
                  <v-tooltip :text="request.comment" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <v-icon
                        v-bind="tooltipProps"
                        icon="mdi-comment-text-outline"
                        size="small"
                      />
                    </template>
                  </v-tooltip>
                </template>
              </div>
            </template>
          </v-list-item>
        </v-list>

        <div v-if="totalPages > 1" class="d-flex justify-center mt-4">
          <v-pagination
            v-model="pagination.page"
            :length="totalPages"
            :total-visible="5"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Review Confirmation Dialog -->
  <v-dialog v-model="showReviewDialog" max-width="500px">
    <v-card>
      <v-card-title>
        {{
          reviewAction === PromotionRequestStatus.APPROVED
            ? $gettext('Approve Promotion')
            : $gettext('Reject Promotion')
        }}
      </v-card-title>
      <v-card-text>
        <p class="mb-4">
          {{
            reviewAction === PromotionRequestStatus.APPROVED
              ? $gettext(
                  'Are you sure you want to approve this promotion request? The item will become a standard catalogue item.',
                )
              : $gettext(
                  'Are you sure you want to reject this promotion request?',
                )
          }}
        </p>
        <v-textarea
          v-model="reviewComment"
          :label="$gettext('Comment (optional)')"
          :placeholder="
            reviewAction === PromotionRequestStatus.REJECTED
              ? $gettext('Provide a reason for rejection...')
              : $gettext('Add any notes...')
          "
          variant="outlined"
          rows="3"
          hide-details
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="grey-darken-1"
          variant="text"
          :disabled="reviewLoading"
          @click.stop="cancelReview"
        >
          {{ $gettext('Cancel') }}
        </v-btn>
        <v-btn
          :color="
            reviewAction === PromotionRequestStatus.APPROVED
              ? 'success'
              : 'warning'
          "
          variant="text"
          :loading="reviewLoading"
          @click.stop="confirmReview"
        >
          {{
            reviewAction === PromotionRequestStatus.APPROVED
              ? $gettext('Approve')
              : $gettext('Reject')
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
